import PropTypes from 'prop-types';
import React, { useContext, useMemo } from 'react';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useWizardService } from '@bingads-webui-react/wizard';
import { Stack } from '@fluentui/react/lib/Stack';
import {
  makeStyles,
  Subtitle1,
  Subtitle2,
  Body1,
  Caption1,
  Caption1Strong,
  mergeClasses,
} from '@fluentui/react-components';
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerInline,
} from '@fluentui/react-drawer';
import { budgetFormatOptions } from '../../constants/budget-format-options';
import { studySummaryDrawerStyles } from '../../page-layout';
import { ImpressionThresholdMessage } from '../impression-threshold-message';
import { SelectedCampaignsAccordion } from './selected-campaigns-accordion';

const useStyles = makeStyles(studySummaryDrawerStyles);

const StudySummaryDrawerInternal = ({ onRemoveCampaign, serverContext }) => {
  const styles = useStyles();
  const { CurrentAccount } = serverContext;
  const { CurrencyCode } = CurrentAccount;
  const i18n = useContext(I18nContext);
  const studySummaryTitle = i18n.getString(_TL_('Experiment summary'));
  const campaignSelectionTitle = i18n.getString(_TL_('Selected campaigns'));
  const totalBudgetLabel = i18n.getString(_TL_('Total budget for selected campaigns'));

  const { state: wizardState } = useWizardService();
  const { selectedCampaignsById } = wizardState.context.wizardData;

  const selectedCampaigns = Object.keys(selectedCampaignsById).map(campaignId => selectedCampaignsById[campaignId]);
  const totalBudgetFormatted = useMemo(() => {
    if (!CurrencyCode) return '--';
    const total = selectedCampaigns.reduce((acc, campaign) => {
      const dailyTargetBudgetAmount = campaign.DailyTargetBudgetAmount ? campaign.DailyTargetBudgetAmount : 0;
      const dailyBudget = campaign.Budget && campaign.Budget.Amount ? campaign.Budget.Amount : dailyTargetBudgetAmount;
      return acc + dailyBudget;
    }, 0);
    return i18n.formatCurrency(total, CurrencyCode, budgetFormatOptions);
  }, [selectedCampaigns, i18n, CurrencyCode]);

  return (
    <DrawerInline open position="end" size="small" className="experiment-summary">
      <DrawerHeader className={mergeClasses(styles.drawer, styles.drawerHeader)}>
        <DrawerHeaderTitle>
          <Subtitle1> {studySummaryTitle} </Subtitle1>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={styles.drawer}>
        <Stack>
          <ImpressionThresholdMessage />
          <Subtitle2 className={styles.subTitle}> {campaignSelectionTitle} </Subtitle2>
          <Body1>
            <div className={mergeClasses(styles.totalBudget, styles.content)}>
              <Caption1Strong>{totalBudgetLabel}</Caption1Strong>
              <Caption1>{totalBudgetFormatted}</Caption1>
            </div>
            <SelectedCampaignsAccordion
              onRemoveCampaign={onRemoveCampaign}
              selectedCampaigns={selectedCampaigns}
            />
          </Body1>
        </Stack>
      </DrawerBody>
    </DrawerInline>
  );
};

StudySummaryDrawerInternal.propTypes = {
  onRemoveCampaign: PropTypes.func.isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      CurrencyCode: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export const StudySummaryDrawer = withServerContext(StudySummaryDrawerInternal);
